import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import TextView from "../../../TextView"; // Assuming you have a TextView component
import { colors } from "../../../../utils";
import LockIcon from "@mui/icons-material/Lock";
import { useDispatch, useSelector } from "react-redux";
import {
  editPaletteTitle,
  lockColor,
  selectColor,
  selectPalette,
  updateColorInPalette,
} from "../../../../redux/reducers/colorPalette";
import EditIcon from "@mui/icons-material/Edit";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Tooltip } from "@mui/material";
import { savePalette } from "../../../../services/api";
import { getWheelId } from "../../../../redux/selectors/segmentBuilderSelectors";
import { toast } from "react-toastify";
import CustomColorPicker from "../../../CustomColorPicker";
import RenamePaletteDialog from "./RenamePaletteDialog";
import { PALETTE_TYPES } from "./constants";
const Container = styled.div`
  margin-bottom: 20px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  width: max-content;
`;

const Title = styled(TextView)`
  font-weight: 500;
  margin-bottom: 5px;
`;

const EditIconWrapper = styled(EditIcon)`
  color: ${colors.darkGrey};
  margin-left: 5px;
  margin-bottom: 4px;
  cursor: pointer;

  &.edit-icon {
    font-size: 16px;
  }
`;

const Card = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border: 1px solid ${colors.lightBorder};
  border-color: ${({ selected }) =>
    selected ? colors.PrimaryPurple : colors.lightBorder};
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
`;

const CirclesWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const CircleWrapper = styled.div`
  width: ${({ selected }) => (selected ? "26px" : "24px")};
  height: ${({ selected }) => (selected ? "26px" : "24px")};
  border-radius: 50%;
  border: 1px solid #98a2b3;
  position: relative;
  padding: ${(props) => (props.locked ? "2px" : 0)};
`;

const CircleBorder = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1px solid black;
  padding: 2px;
`;

const Circle = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${(props) => props.color || colors.defaultColor};
`;

const IconsWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const LockIconWrapper = styled.div`
  background-color: white;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 2px;
  border: 1px solid #98a2b3;
  right: -4px;
  top: -4px;
`;

export const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

export default function PaletteItem({
  palette,
  index,
  handleGenerateSchemes,
  type = "color",
  paletteType,
}) {
  const dispatch = useDispatch();
  const wheelId = useSelector(getWheelId);

  const { paletteName, palette_name, colors: paletteColors, id, selected } = palette;
  const selectedColor = paletteColors.find((color) => color.selected);



  const [showColorPicker, setShowColorPicker] = useState(false);
  const [temporaryColor, setTemporaryColor] = useState(selectedColor?.hex);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [error, setError] = useState("");

  const handleLockColor = () => {
    if (selectedColor) {
      dispatch(
        lockColor({
          paletteType,
          type,
          colorId: selectedColor.id,
          isLocked: !selectedColor.locked, // Toggle locked state
        })
      );
    }
  };

  const handleSavePalette = async () => {
    const TAB_ENUMS = {
      color: "1",
      image: "2",
      prompt: "3",
    };
    setError("");
    try {
      const response = await savePalette({
        id: typeof id === 'string' ? undefined : id,
        colors: paletteColors,
        palette_name: paletteName || palette_name,
        wheel_id: wheelId,
        type: 1,
        usage_type: paletteType == PALETTE_TYPES.SEGMENT ? 2 : 1,
        tab_type: TAB_ENUMS[type],
        metadata: {
          color: type == "color" ? palette.colors[0]?.hex : undefined,
          prompt: type == "prompt" ? palette.prompt : undefined,
          image: type == "image" ? palette.imageUrl : undefined,
        },
      });

      if (response.data.success) {
        toast.success("Palette saved successfully!");
      } else {
        // toast.error(response.data.message);
        setError(response.data.message);
        setTimeout(() => setError(""), 10000);
      }
    } catch (error) {
      console.error("Error saving palette:", error);
    }
  };

  const handleEditColor = () => {
    setTemporaryColor(selectedColor?.hex);
    setShowColorPicker(true);
  };

  const handleColorChange = (newColor) => {
    setTemporaryColor(newColor);
  };

  const handleConfirmColor = () => {
    if (selectedColor) {
      dispatch(
        updateColorInPalette({
          paletteType,
          paletteId: id,
          colorId: selectedColor.id,
          updatedColor: { hex: temporaryColor },
          type,
        })
      );
    }
    setShowColorPicker(false);
  };

  const handleSelectColor = (color) => {
    dispatch(selectColor({ paletteType, type, colorId: color.id }));
  };

  const handleTitleEdit = () => {
    setShowRenameModal(true);
  };

  const handleConfirmRename = (newTitle) => {
    dispatch(
      editPaletteTitle({
        paletteType,
        paletteId: id,
        newTitle: newTitle,
        type,
      })
    );
    setShowRenameModal(false);
  };

  const handleCloseRename = () => {
    setShowRenameModal(false);
  };

  return (
    <div style={{ position: "relative" }}>
      <Container>
        <Header>
          <TitleContainer>
            <Title variant="largeText" style={{ WebkitLineClamp: 1 }}>
              {paletteName || palette_name}
            </Title>

            <Tooltip title="Edit title" placement="top">
              <EditIconWrapper
                className="edit-icon"
                onClick={handleTitleEdit}
              />
            </Tooltip>
          </TitleContainer>
          {selectedColor && type !== "image" && (
            <div>
              {selectedColor.locked ? (
                <Tooltip title="Unlock color" placement="top">
                  <LockOpenIcon
                    onClick={handleLockColor}
                    style={{
                      fontSize: 18,
                      color: colors.darkGrey,
                      marginRight: 5,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Lock color" placement="top">
                  <LockIcon
                    onClick={handleLockColor}
                    style={{
                      fontSize: 18,
                      color: colors.darkGrey,
                      marginRight: 5,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              )}

              <Tooltip title="Edit color" placement="top">
                <EditIcon
                  onClick={handleEditColor}
                  style={{ fontSize: 18, color: colors.darkGrey }}
                />
              </Tooltip>
            </div>
          )}
        </Header>

        <Card
          selected={selected}
          onClick={() =>
            dispatch(selectPalette({ paletteType, type, paletteId: id }))
          }
        >
          <div>
            <CirclesWrapper>
              {paletteColors.map((color, index) => (
                <Tooltip
                  placement="top"
                  title={
                    type == "color" && index == 0 ? "Segment Color" : color.hex
                  }
                >
                  <CircleWrapper
                    key={index}
                    selected={color.selected}
                    locked={color.locked}
                    onClick={() =>
                      handleSelectColor(color)
                    }
                    style={{
                      cursor:
                        "pointer",
                    }}
                  >
                    {color.locked && (
                      <LockIconWrapper>
                        <LockIcon
                          style={{ fontSize: 10, color: colors.darkGrey }}
                        />
                      </LockIconWrapper>
                    )}
                    {color.selected ? (
                      <CircleBorder>
                        <Circle color={color.hex} />
                      </CircleBorder>
                    ) : (
                      <Circle color={color.hex} />
                    )}
                  </CircleWrapper>
                </Tooltip>
              ))}
            </CirclesWrapper>
          </div>
          <IconsWrapper>
            {type !== "image" && (
              <Tooltip title="Regenerate palette" placement="top">
                <LoopOutlinedIcon
                  style={{ fontSize: 20, color: colors.darkGrey }}
                  onClick={() => handleGenerateSchemes(palette)}
                />
              </Tooltip>
            )}
            <Tooltip title="Save palette to library" placement="top">
              <SaveOutlinedIcon
                style={{ fontSize: 20, color: colors.darkGrey }}
                onClick={handleSavePalette}
              />
            </Tooltip>
          </IconsWrapper>
        </Card>
        {showColorPicker && (
          <CustomColorPicker
            visible={showColorPicker}
            onClose={() => setShowColorPicker(false)}
            onConfirm={handleConfirmColor}
            color={temporaryColor}
            onColorChange={handleColorChange}
          />
        )}
      </Container>
      {!!error && (
        <div style={{ position: "absolute", bottom: -20, right: 0 }}>
          <TextView variant="tag" color={colors.error}>
            *{error}
          </TextView>
        </div>
      )}

      <RenamePaletteDialog
        open={showRenameModal}
        onClose={handleCloseRename}
        onConfirm={handleConfirmRename}
        initialValue={paletteName || palette_name}
      />
    </div>
  );
}
